/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useLoggingSettingsForm.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing logging settings form state.
────────────────────────────────────────────────────────────────*/

import { useCallback } from 'react';
import type {
  LoggingSettings,
  ConsoleSettings,
  FileLogging,
  FileProfile,
  FileProfileMapping
} from '../types';

export function useLoggingSettingsForm(
  formData: LoggingSettings,
  setFormData: React.Dispatch<React.SetStateAction<LoggingSettings>>
) {
  const updateConsoleSettings = useCallback((field: keyof ConsoleSettings, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      consoleSettings: {
        ...prev.consoleSettings,
        [field]: value
      }
    }));
  }, [setFormData]);

  const updateFileLogging = useCallback((field: keyof FileLogging, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      fileLogging: {
        ...prev.fileLogging,
        [field]: value
      }
    }));
  }, [setFormData]);

  const updateProfile = useCallback((index: number, field: keyof FileProfile, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      fileLogging: {
        ...prev.fileLogging,
        profiles: prev.fileLogging.profiles.map((profile, i) =>
          i === index ? { ...profile, [field]: value } : profile
        )
      }
    }));
  }, [setFormData]);

  const updateProfileMapping = useCallback((
    profileIndex: number,
    mappingIndex: number,
    field: keyof FileProfileMapping,
    value: unknown
  ) => {
    setFormData(prev => ({
      ...prev,
      fileLogging: {
        ...prev.fileLogging,
        profiles: prev.fileLogging.profiles.map((profile, i) =>
          i === profileIndex
            ? {
                ...profile,
                mappings: profile.mappings.map((mapping, j) =>
                  j === mappingIndex ? { ...mapping, [field]: value } : mapping
                )
              }
            : profile
        )
      }
    }));
  }, [setFormData]);

  const updateEnabled = useCallback((value: boolean) => {
    setFormData(prev => ({ ...prev, enabled: value }));
  }, [setFormData]);

  return {
    updateConsoleSettings,
    updateFileLogging,
    updateProfile,
    updateProfileMapping,
    updateEnabled
  };
}

